import React, { MouseEventHandler, useEffect, useRef } from "react";

import classNames from "classnames/bind";
import IconButton from "common/blocks/IconButton";
import { Sidebar, SidebarTypes } from "common/providers/modal/useSidebar";

import styles from "../Sidebar.module.scss";

const cx = classNames.bind(styles);

type WithoutFooter = {
    footerComponent?: never;
    footerProps?: never;
};

type WithFooter = {
    footerComponent: ((...args: any[]) => React.ReactElement) | null;
    footerProps: Record<string, unknown>;
};

type FooterUnionProps = WithFooter | WithoutFooter;

export type SidebarPanelProps = {
    title: string;
    onClose?: MouseEventHandler<HTMLButtonElement> & (() => void);
    onGoBack?: MouseEventHandler<HTMLButtonElement> & (() => void);
    previousModal?: Sidebar;
    children?: React.ReactNode;
    className?: string;
    disableScroll?: boolean;
    headingClassname?: string;
    sidebarType?: SidebarTypes;
    resetScrollPosition?: boolean;
} & FooterUnionProps;

const Panel = React.forwardRef(
    (
        {
            title,
            onClose = () => {},
            onGoBack = () => {},
            previousModal,
            footerComponent: Footer,
            children,
            className,
            disableScroll,
            footerProps,
            headingClassname,
            sidebarType,
            resetScrollPosition = true,
        }: SidebarPanelProps,
        ref: any
    ) => {
        const contentRef = useRef<HTMLDivElement | null>(null);

        const hasFooter = !!Footer;

        useEffect(() => {
            resetScrollPosition && contentRef.current?.scrollTo(0, 0);
        }, [resetScrollPosition]);

        return (
            <div
                className={cx("sidebar", className)}
                ref={ref}
                role="complementary"
                aria-label="Sidebar"
            >
                <div
                    className={cx("heading", headingClassname, {
                        filterHeading: sidebarType === SidebarTypes.FILTER,
                    })}
                    aria-roledescription=""
                >
                    <div className={cx("goBackAndTitle")}>
                        {previousModal && (
                            <IconButton
                                className={cx("goBackButton")}
                                title="Go back"
                                aria-label="Go back"
                                onClick={onGoBack}
                                icon="ChevronLeft"
                            />
                        )}
                        <span className={cx("title")}>{title}</span>
                    </div>

                    <IconButton
                        title="Close Modal"
                        aria-label="Close Modal"
                        onClick={onClose}
                        icon="Close"
                        role="button"
                    />
                </div>
                <div
                    id="sidebar-content"
                    ref={contentRef}
                    className={cx("content", { disableScroll, hasFooter })}
                >
                    {children}
                </div>
                {hasFooter && Footer && (
                    <div className={cx("footer")}>
                        <Footer {...footerProps} />
                    </div>
                )}
            </div>
        );
    }
);

Panel.displayName = "Panel";

export default Panel;
